// Auth state — Rialto platform cookie-session pattern.
//
// The server's httpOnly `wos_session` cookie is the canonical auth state;
// this store is just a render-time mirror so components (NavBar, RoleGate,
// etc.) can read user info without hitting the network on every render.
//
// Bearer-token machinery removed: no accessToken to read, no
// expiresAt to track, no isTokenExpired check. The server handles all
// session lifetime via sealed_session + refresh-token rotation +
// Redis swap cache. Client just calls /api/v1/users/me on mount (see
// WorkOSProvider) and the cookie tags along for every subsequent
// request automatically (axios `withCredentials: true`).
//
// Interface preserved for backward compatibility with a few legacy
// consumers (ArtifactsTab, PdfPageViewer) that still read `.accessToken`
// for raw-fetch authorization headers. The value is always empty —
// those manual fetches send no Authorization header, the server reads
// the cookie, end result is identical.

import { create } from 'zustand'
import type { User, AuthSession } from '@/types/domain'
import { apiClient } from '@/lib/api-client'

interface AuthState {
  user:            User | null
  // Legacy fields kept for backward compatibility with callers that
  // read them. Always empty/null in the cookie-auth world.
  accessToken:     string | null
  expiresAt:       number | null
  isLoading:       boolean
  isAuthenticated: boolean

  // Impersonation state (dev/test only — set after POST /admin/impersonate-role or /impersonate-user)
  impersonatedRole:      string | null
  impersonatedUserName:  string | null   // null = role-only mode; set = user-impersonation mode
  impersonatedUserEmail: string | null
  originalRole:          string | null
  isImpersonating:       boolean

  // Live permissions for the current effective role — fetched from
  // GET /api/v1/users/me/permissions after login and after every
  // impersonation switch. Empty object until the first fetch completes
  // (gating logic falls back to the static mirror in
  // @/config/role-permissions until then).
  permissions:        Record<string, string>
  permissionsLoaded:  boolean

  // Setters used by WorkOSProvider on mount and by logout flows.
  setUser:     (user: User | null) => void
  setLoading:  (loading: boolean) => void
  // Async because we must hit POST /admin/impersonate-role/exit when the
  // user is currently impersonating — otherwise the impersonation_session
  // DB row + httpOnly cookie outlive the wos_session and the next sign-in
  // (or another session reusing the cookie) inherits a stale role override.
  logout:      () => Promise<void>

  // Impersonation actions
  setImpersonation:   (impersonatedRole: string, originalRole: string, userName?: string | null, userEmail?: string | null) => void
  clearImpersonation: () => void

  // Permission fetch — call after login + after impersonation toggles
  fetchPermissions:   () => Promise<void>

  // Legacy setters/checkers — preserved as no-ops/shims so any code we
  // missed during the migration doesn't TypeError at runtime. New code
  // should use setUser/logout directly.
  setSession:     (session: AuthSession) => void
  isTokenExpired: () => boolean
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user:            null,
  accessToken:     null,
  expiresAt:       null,
  isLoading:       true,
  isAuthenticated: false,

  impersonatedRole:      null,
  impersonatedUserName:  null,
  impersonatedUserEmail: null,
  originalRole:          null,
  isImpersonating:       false,

  permissions:       {},
  permissionsLoaded: false,

  setUser: (user: User | null) => {
    set({
      user,
      isAuthenticated: user !== null,
      isLoading:       false,
    })
    // Prime live permissions immediately after the user is hydrated.
    if (user) { void get().fetchPermissions() }
  },

  setLoading: (loading: boolean) => set({ isLoading: loading }),

  logout: async () => {
    // If the user is currently impersonating another role, tell the
    // server to drop the impersonation_sessions row + delete the
    // httpOnly impersonation_session cookie BEFORE we wipe local state
    // and the wos_session is invalidated. We swallow errors so a
    // transient network failure can't prevent the user from signing out.
    if (get().isImpersonating) {
      try {
        await apiClient.post('/admin/impersonate-role/exit')
      } catch {
        // best-effort — proceed with logout regardless
      }
    }
    // Page-level Maximize toggles (Pipeline Overview, Analyst Directory,
    // Deal Library, 2026-09-29) persist in localStorage via
    // useLocalStorageState precisely so they survive reload/tab-close/
    // browser restart -- Michael's explicit ask was that they clear ONLY
    // on logout (here) or the user manually restoring. Keep these three
    // key names in sync with the useLocalStorageState('...', false) calls
    // in PipelinePage.tsx / AnalystLibraryPage.tsx / DealLibraryNewPage.tsx.
    try {
      localStorage.removeItem('pipeline.maximized')
      localStorage.removeItem('analystLibrary.maximized')
      localStorage.removeItem('deallib.maximized')
    } catch {
      // storage unavailable (private browsing) -- non-fatal
    }
    set({
      user:              null,
      accessToken:       null,
      expiresAt:         null,
      isAuthenticated:   false,
      isLoading:         false,
      impersonatedRole:      null,
      impersonatedUserName:  null,
      impersonatedUserEmail: null,
      originalRole:          null,
      isImpersonating:       false,
      permissions:       {},
      permissionsLoaded: false,
    })
  },

  setImpersonation: (impersonatedRole: string, originalRole: string, userName?: string | null, userEmail?: string | null) => {
    set({ impersonatedRole, originalRole, isImpersonating: true, impersonatedUserName: userName ?? null, impersonatedUserEmail: userEmail ?? null })
    // Re-fetch — server may already have an impersonation_session cookie,
    // and the new effective role's permissions may differ.
    void get().fetchPermissions()
  },

  clearImpersonation: () => {
    set({ impersonatedRole: null, originalRole: null, isImpersonating: false })
    void get().fetchPermissions()
  },

  fetchPermissions: async () => {
    try {
      // `?bust=1` drops the caller's role from the API's in-process
      // permission cache before the recompute. This is what makes a
      // WorkOS role change (or an impersonation toggle, or any admin-UI
      // permission edit) take effect on THIS request rather than waiting
      // up to 60 seconds for the cache TTL to expire. See rbac.py header
      // comment for the full rationale.
      const { data } = await apiClient.get<Record<string, string>>(
        '/users/me/permissions?bust=1',
      )
      set({ permissions: data ?? {}, permissionsLoaded: true })
    } catch {
      // Leave existing permissions in place on transient failure.
      // The static fallback in @/config/role-permissions covers the not-yet-loaded case.
    }
  },

  // ── Legacy shims ──────────────────────────────────────────────
  setSession: (session: AuthSession) => {
    // Old AuthSession shape carried { user, access_token, expires_at }.
    // In cookie-auth, we only need the user; cookie is set server-side.
    set({
      user:            session.user,
      isAuthenticated: true,
      isLoading:       false,
    })
  },
  isTokenExpired: () => {
    // In cookie-auth the server handles expiry; a 401 response triggers
    // the api-client interceptor to redirect through /auth/login. Always
    // return false from this legacy check.
    void get
    return false
  },
}))

// ── Effective-role hook ──────────────────────────────────────────────────────
//
// Returns the role that should be used for permission/visibility checks
// throughout the UI: the impersonated role when impersonation is active,
// otherwise the real signed-in user's role. Uppercased for consistency
// with the canonical role names in the roles table (ADMIN, PM, ANALYST, …).
//
// IMPORTANT: this is for *display gating* (which nav items to show, which
// admin-only buttons to render, etc). It is NOT a security boundary —
// the server enforces RBAC against the real session in rbac.py. The one
// place we deliberately use the REAL role instead is the "Switch Role"
// button in TopBar, so an impersonating admin can always switch back.

export function useEffectiveRole(): string | null {
  const user             = useAuthStore(s => s.user)
  const isImpersonating  = useAuthStore(s => s.isImpersonating)
  const impersonatedRole = useAuthStore(s => s.impersonatedRole)
  if (isImpersonating && impersonatedRole) return impersonatedRole.toUpperCase()
  if (!user) return null
  return (user.role ?? '').toString().toUpperCase() || null
}
